import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { EDGE_NAVY } from "../../lib/edge";
import MortgageDisclosure from "../../components/MortgageDisclosure";
import Seo from "../../components/Seo";

const PROGRAMS = [
  {
    id: "conventional",
    name: "Conventional",
    tagline: "The flexible workhorse — best credit, lowest cost.",
    body: "Backed by Fannie Mae or Freddie Mac. Great for borrowers with 620+ credit. Down payments from 3% (HomeReady / Home Possible) to 20%+ to avoid PMI.",
    bullets: [
      "As little as 3% down (first-time buyer programs)",
      "PMI required under 20% — auto-cancels at 78% LTV",
      "Fixed (15/20/30 yr) and ARM options",
      "Loan limits up to ~$806,500 in most TX counties (2025)",
    ],
  },
  {
    id: "fha",
    name: "FHA",
    tagline: "Low down payment, flexible credit thresholds.",
    body: "Insured by the Federal Housing Administration. Designed for first-time buyers and borrowers rebuilding credit. Down payment as low as 3.5%.",
    bullets: [
      "3.5% down with 580+ credit (10% with 500–579)",
      "More forgiving on debt-to-income and credit history",
      "MIP (mortgage insurance premium) for the life of the loan in most cases",
      "Strict property condition standards (FHA appraisal)",
    ],
  },
  {
    id: "va",
    name: "VA Loan",
    tagline: "0% down for veterans, active duty, and eligible spouses.",
    body: "Guaranteed by the Department of Veterans Affairs. One of the most generous loan programs available — no down payment, no PMI.",
    bullets: [
      "0% down payment",
      "No private mortgage insurance",
      "Competitive interest rates",
      "Funding fee may apply (waived for service-connected disability)",
    ],
  },
  {
    id: "jumbo",
    name: "Jumbo",
    tagline: "For loans above conforming limits — Houston luxury.",
    body: "For Houston neighborhoods like River Oaks, Memorial, and West University, jumbo loans handle purchase prices that exceed conventional conforming limits.",
    bullets: [
      "Loan amounts above ~$806,500 (2025 TX conforming limit)",
      "Typically requires 700+ credit and 10–20% down",
      "Reserves (cash on hand) typically required",
      "Fixed and ARM options; high-balance pricing",
    ],
  },
  {
    id: "usda",
    name: "USDA",
    tagline: "0% down in eligible rural and suburban areas.",
    body: "Backed by the U.S. Department of Agriculture. Designed for low-to-moderate income borrowers in eligible (often suburban) areas.",
    bullets: [
      "0% down payment",
      "Income limits apply (typically 115% of area median)",
      "Property must be in a USDA-eligible area",
      "Lower mortgage insurance than FHA",
    ],
  },
  {
    id: "first-time",
    name: "First-Time Buyer",
    tagline: "Programs that combine low down + assistance.",
    body: "Combines Fannie Mae HomeReady, Freddie Mac Home Possible, and Texas Department of Housing & Community Affairs (TDHCA) assistance programs to lower out-of-pocket costs.",
    bullets: [
      "3% down options",
      "TDHCA My First Texas Home — DPA grants & second-lien options",
      "Reduced PMI for income-eligible borrowers",
      "Education courses required by some programs",
    ],
  },
  {
    id: "203k",
    name: "FHA 203(k) Rehab",
    tagline: "Buy and renovate with one loan — fixer-uppers welcome.",
    body: "The FHA 203(k) program lets you finance the purchase price AND the cost of repairs/renovation in a single mortgage. Perfect for distressed properties, dated homes, or buyers who want to customize before move-in.",
    bullets: [
      "Standard 203(k) — major structural repairs, room additions",
      "Limited 203(k) — up to $35,000 in cosmetic & minor repairs (no structural)",
      "3.5% down (same as standard FHA)",
      "One closing, one monthly payment — repairs financed at mortgage rates",
      "Funds released in draws as work is completed",
      "Property does NOT need to meet FHA standards at purchase",
    ],
  },
  {
    id: "dscr",
    name: "DSCR",
    tagline: "Qualify on the property's cash flow — not your tax returns.",
    body: "Debt-Service Coverage Ratio loans qualify rental properties based on the income the property itself generates. Ideal for self-employed investors, LLC-titled properties, and portfolio builders who don't want to document personal income.",
    bullets: [
      "No personal income, W-2s, or tax returns required",
      "Qualify based on the property's rent vs. PITIA (DSCR ≥ 1.0 typical, 0.75+ accepted)",
      "Title in your name or an LLC",
      "Down payments typically 20–25% · 30-yr fixed, ARM, interest-only available",
      "Unlimited properties · scale your portfolio",
      "Short-term rental (Airbnb / VRBO) income accepted on many programs",
    ],
  },
  {
    id: "investor",
    name: "Investor Loans",
    tagline: "Full suite of non-QM products built for real estate investors.",
    body: "Beyond DSCR, we offer Bank Statement loans, Asset-Based / Asset Depletion qualification, Fix-and-Flip / Bridge financing, and portfolio loans for multi-unit and mixed-use properties. Designed for serious Houston-area investors.",
    bullets: [
      "Bank Statement loans (12 or 24 months) for self-employed buyers",
      "Asset-Based / Asset Depletion qualification — no income docs",
      "Fix-and-Flip & Bridge loans — short-term, ARV-based",
      "1–4 unit residential, 5+ unit multifamily, and mixed-use available",
      "Foreign National programs (no SSN / no US credit required)",
      "Cash-out refinance for portfolio recycling",
    ],
  },
  {
    id: "reverse",
    name: "Reverse Mortgage (HECM)",
    tagline: "Tap home equity in retirement — no monthly mortgage payment.",
    body: "A Home Equity Conversion Mortgage (HECM) lets homeowners age 62+ convert a portion of their home equity into tax-free cash, a line of credit, or monthly income — while still owning and living in the home. No monthly mortgage payments are required (you continue to pay taxes, insurance, and maintenance).",
    bullets: [
      "Must be 62 or older (youngest borrower)",
      "Home must be your primary residence",
      "No monthly mortgage payments — loan repaid when home is sold or owner moves out",
      "Receive funds as lump sum, monthly income, line of credit, or combination",
      "Proceeds are tax-free and don't affect Social Security or Medicare",
      "FHA-insured (HECM) — most common · non-recourse (you/heirs never owe more than the home's value)",
      "HUD-approved counseling required before applying",
    ],
  },
  {
    id: "refinance",
    name: "Refinance",
    tagline: "Lower your rate, change your term, or pull cash out.",
    body: "Refinancing replaces your current mortgage with a new one — typically to reduce your monthly payment, shorten your term, eliminate PMI, or extract equity for renovations, debt consolidation, or investments.",
    bullets: [
      "Rate-and-Term — lower your interest rate or change loan length",
      "Cash-Out Refinance — borrow against your home equity",
      "FHA Streamline & VA IRRRL — minimal-doc refinances with no appraisal in many cases",
      "Conventional, FHA, VA, Jumbo, and Non-QM refinance options",
      "Drop PMI once you reach 80% LTV",
      "Texas (a)(6) cash-out rules respected — 80% max LTV on primary residence",
    ],
  },
  {
    id: "heloc",
    name: "HELOC / Home Equity Loan",
    tagline: "Use your equity without touching your first mortgage.",
    body: "Keep your existing low-rate first mortgage and tap your equity through a second lien. A HELOC works like a credit card secured by your home (variable rate, draw period); a Home Equity Loan is a fixed-rate lump sum with predictable payments.",
    bullets: [
      "HELOC — revolving line of credit, 10-year draw + 20-year repayment typical",
      "Home Equity Loan — fixed rate, fixed term lump sum",
      "Up to 80% combined LTV on primary residence (Texas Constitution)",
      "Use for renovations, debt consolidation, tuition, emergencies",
      "Interest may be tax-deductible if used for home improvement (consult your CPA)",
      "Faster to close than a first-lien refinance",
    ],
  },
  {
    id: "construction",
    name: "Construction-to-Permanent",
    tagline: "Finance the build and the mortgage in one loan.",
    body: "A single-close construction-to-permanent (C2P) loan funds the lot purchase and construction draws during the build, then automatically converts to a permanent 15- or 30-year mortgage when the home is complete. One application, one closing, one set of fees.",
    bullets: [
      "Single close = lower fees than two separate loans",
      "Interest-only payments during construction (typically 9–12 months)",
      "Converts to a permanent fixed or ARM mortgage at completion",
      "Conventional, FHA, VA, and Jumbo construction options",
      "10–20% down typical · custom homes, tear-downs, major additions",
      "Builder must be approved · contingency reserve required",
    ],
  },
  {
    id: "physician",
    name: "Doctor / Physician Loan",
    tagline: "Built for MDs, DOs, dentists, and residents.",
    body: "Specialty mortgage programs that recognize the unique financial picture of medical professionals — high student debt, late-career start, and strong future earning potential. Designed so a young doctor can buy a home before paying off training debt.",
    bullets: [
      "0–10% down up to high loan amounts (often $1M–$2M+)",
      "No PMI — even with low down payments",
      "Student loan debt calculated favorably (often excluded from DTI)",
      "Eligible: MD, DO, DDS, DMD, DPM, OD; residents & fellows on many programs",
      "Employment-contract income accepted (start within 60–90 days)",
      "Primary residence only on most programs",
    ],
  },
  {
    id: "itin",
    name: "ITIN Loan",
    tagline: "Homeownership for borrowers without a Social Security Number.",
    body: "ITIN (Individual Taxpayer Identification Number) loans allow non-citizen buyers — including many DACA recipients and immigrants paying U.S. taxes — to qualify for a mortgage using their ITIN instead of an SSN. A path to homeownership for hard-working Houstonians without traditional documentation.",
    bullets: [
      "No Social Security Number required — ITIN accepted",
      "2 years of U.S. tax returns filed under the ITIN",
      "Down payments typically 15–25%",
      "Primary residence, second homes, and many investment properties eligible",
      "Standard credit underwriting · alternative credit (rent, utilities) considered when needed",
      "Fixed-rate 15/30 yr and ARM options available",
    ],
  },
];

export default function LoanPrograms() {
  return (
    <div className="bg-stone-50 min-h-screen" data-testid="loan-programs-page">
      <Seo
        title="Mortgage Loan Programs"
        description="Fifteen loan programs through Edge Home Finance: Conventional, FHA, VA, Jumbo, USDA, First-Time Buyer, FHA 203(k), DSCR, Investor, Reverse Mortgage (HECM), Refinance, HELOC, Construction-to-Permanent, Physician, and ITIN. NMLS #891464."
        path="/loan-programs"
      />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-12">
        <div className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: EDGE_NAVY }}>
          Edge Home Finance · Programs
        </div>
        <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter text-[#1A1A1A] max-w-3xl">
          The right loan for <em className="not-italic" style={{ color: EDGE_NAVY }}>your</em> situation.
        </h1>
        <p className="text-[#555] mt-5 max-w-2xl text-lg">
          Fifteen loan programs covering every Houston buyer, investor, professional, and retiree. Not sure which fits? Pierre will help.
        </p>
      </section>

      {/* Quick links */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10">
        <div className="flex flex-wrap gap-2">
          {PROGRAMS.map((p) => (
            <a
              key={p.id}
              href={`#${p.id}`}
              className="text-xs font-medium px-4 py-2 rounded-full bg-white border border-gray-200 hover:border-[#E63946] hover:text-[#E63946]"
              data-testid={`programs-jump-${p.id}`}
            >
              {p.name}
            </a>
          ))}
        </div>
      </section>

      {/* Program cards */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 space-y-6">
        {PROGRAMS.map((p, i) => (
          <article
            key={p.id}
            id={p.id}
            className="bg-white border border-gray-200 rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-12 scroll-mt-28"
          >
            <div
              className="lg:col-span-4 p-8 lg:p-10 flex flex-col justify-center text-white"
              style={{ background: i % 2 === 0 ? EDGE_NAVY : "#1A1A1A" }}
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/70 mb-2">
                Program {String(i + 1).padStart(2, "0")}
              </div>
              <div className="font-display text-4xl font-semibold tracking-tighter">{p.name}</div>
              <p className="text-sm text-white/85 mt-4 leading-relaxed">{p.tagline}</p>
            </div>
            <div className="lg:col-span-8 p-8 lg:p-10">
              <p className="text-[#333] leading-relaxed">{p.body}</p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-[#1A1A1A]">
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                      style={{ background: `${EDGE_NAVY}1A`, color: EDGE_NAVY }}
                    >
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-7">
                <Link
                  to="/pre-approval"
                  state={{ program: p.name }}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#E63946] hover:gap-3 transition-all"
                >
                  Get pre-approved for {p.name} <ArrowRight className="w-4 h-4" />
                </Link>
                {p.id === "dscr" && (
                  <Link
                    to="/dscr-calculator"
                    className="inline-flex items-center gap-2 text-sm font-semibold hover:gap-3 transition-all"
                    style={{ color: EDGE_NAVY }}
                    data-testid="loan-programs-dscr-calc-link"
                  >
                    Run the DSCR calculator <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* CTA strip */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="bg-[#1A1A1A] text-white rounded-2xl p-10 lg:p-14 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-light tracking-tighter">
            Not sure which program is right for you?
          </h2>
          <p className="text-white/80 mt-4 max-w-xl mx-auto">
            5 minutes with Pierre and you'll know exactly which program fits — and what your monthly payment looks like.
          </p>
          <Link
            to="/pre-approval"
            className="inline-block mt-7 bg-[#E63946] hover:bg-[#c9313d] text-white px-8 py-3.5 rounded-md font-semibold text-sm"
            data-testid="programs-cta"
          >
            Start Pre-Approval
          </Link>
        </div>
      </section>

      <MortgageDisclosure />
    </div>
  );
}
